import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Cloud, CloudOff, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { userService } from '@/services/userService';
import { recommendationService } from '@/services/recommendationService';
import { Tag, Recipe } from '@/types';
import RecipeCard from '@/components/RecipeCard';

const commonAllergens = [
    'Leche',
    'Huevos',
    'Pescado',
    'Mariscos',
    'Nueces',
    'Cacahuates',
    'Trigo/Gluten',
    'Soya',
    'Ajonjolí'
];

const commonDislikes = [
    'Cilantro',
    'Cebolla',
    'Ajo',
    'Chile picante',
    'Aguacate',
    'Jitomate',
    'Frijoles',
    'Queso',
    'Crema',
    'Nopales',
    'Epazote',
    'Chiles en vinagre'
];

// Mapeo de tags nutricionales disponibles
const nutritionalTags: Tag[] = [
    Tag.ALTA_PROTEINA,
    Tag.BAJO_EN_CARBOS,
    Tag.BAJO_EN_CALORIAS,
    Tag.BAJO_EN_GRASAS,
    Tag.VEGETARIANA,
    Tag.VEGANA,
    Tag.GLUTEN_FREE,
    Tag.SIN_LACTOSA,
    Tag.KETO,
    Tag.ALTA_FIBRA
];

const Profile = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { isLoggedIn, token } = useAuth();
    const [allergies, setAllergies] = useState<string[]>([]);
    const [dislikes, setDislikes] = useState<string[]>([]);
    const [nutritionalPreferences, setNutritionalPreferences] = useState<Tag[]>([]);
    const [saving, setSaving] = useState(false);
    const [loadingRecommendations, setLoadingRecommendations] = useState(false);
    const [recommendations, setRecommendations] = useState<Recipe[]>([]);

    const [personalInfo, setPersonalInfo] = useState({
        age: '',
        height: '',
        weight: '',
        gender: '',
        activityLevel: '',
        weightGoal: 'maintain'
    });

    useEffect(() => {
        const loadUserProfile = async () => {
            if (isLoggedIn && token) {
                try {
                    const userProfile = await userService.getProfile(token);
                    console.log('📥 Perfil cargado del backend:', userProfile);

                    // ✅ MANEJAR DATOS UNDEFINED SEGURO
                    if (userProfile) {
                        if (userProfile.allergies) setAllergies(userProfile.allergies);
                        if (userProfile.dislikedIngredients) setDislikes(userProfile.dislikedIngredients);
                        if (userProfile.nutritionalPreferences) setNutritionalPreferences(userProfile.nutritionalPreferences);

                        if (userProfile.weightGoal) {
                            setPersonalInfo(prev => ({
                                ...prev,
                                weightGoal: userProfile.weightGoal.toLowerCase()
                            }));
                        }

                        // Cargar otros datos personales si existen
                        if (userProfile.age) setPersonalInfo(prev => ({ ...prev, age: userProfile.age.toString() }));
                        if (userProfile.height) setPersonalInfo(prev => ({ ...prev, height: userProfile.height.toString() }));
                        if (userProfile.weight) setPersonalInfo(prev => ({ ...prev, weight: userProfile.weight.toString() }));
                        if (userProfile.gender) setPersonalInfo(prev => ({ ...prev, gender: userProfile.gender }));
                        if (userProfile.activityLevel) setPersonalInfo(prev => ({ ...prev, activityLevel: userProfile.activityLevel }));
                    } else {
                        console.warn('⚠️ userProfile es null o undefined');
                        loadFromLocalStorage();
                    }

                } catch (error) {
                    console.error('Error loading profile from backend:', error);
                    // Fallback a localStorage
                    loadFromLocalStorage();
                }
            } else {
                loadFromLocalStorage();
            }
        };

        const loadFromLocalStorage = () => {
            const savedProfile = localStorage.getItem('userProfile');
            if (savedProfile) {
                try {
                    const profileData = JSON.parse(savedProfile);
                    console.log('📥 Perfil cargado de localStorage:', profileData);

                    if (profileData.allergies) setAllergies(profileData.allergies);
                    if (profileData.dislikes) setDislikes(profileData.dislikes);
                    if (profileData.personalInfo) setPersonalInfo(profileData.personalInfo);
                    if (profileData.nutritionalPreferences) setNutritionalPreferences(profileData.nutritionalPreferences);
                } catch (error) {
                    console.error('Error loading profile from localStorage:', error);
                }
            }
        };

        loadUserProfile();
    }, [isLoggedIn, token]);

    const calculateRecommendedCalories = () => {
        const { age, height, weight, gender, activityLevel, weightGoal } = personalInfo;

        if (!age || !height || !weight || !gender || !activityLevel) {
            return 0;
        }

        let bmr = 0;
        if (gender === 'male') {
            bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseFloat(age) + 5;
        } else {
            bmr = 10 * parseFloat(weight) + 6.25 * parseFloat(height) - 5 * parseFloat(age) - 161;
        }

        const activityFactors: { [key: string]: number; } = {
            'sedentary': 1.2,
            'light': 1.375,
            'moderate': 1.55,
            'very': 1.725,
            'extreme': 1.9
        };

        let maintenanceCalories = Math.round(bmr * activityFactors[activityLevel]);

        if (weightGoal === 'lose') {
            maintenanceCalories -= 500;
        } else if (weightGoal === 'gain') {
            maintenanceCalories += 500;
        }

        return maintenanceCalories;
    };

    const handleAllergyChange = (allergen: string, checked: boolean) => {
        if (checked) {
            setAllergies(prev => [...prev, allergen]);
        } else {
            setAllergies(prev => prev.filter(item => item !== allergen));
        }
    };

    const handleDislikeChange = (dislike: string, checked: boolean) => {
        if (checked) {
            setDislikes(prev => [...prev, dislike]);
        } else {
            setDislikes(prev => prev.filter(item => item !== dislike));
        }
    };

    const handleNutritionalPreferenceChange = (tag: Tag, checked: boolean) => {
        if (checked) {
            setNutritionalPreferences(prev => [...prev, tag]);
        } else {
            setNutritionalPreferences(prev => prev.filter(item => item !== tag));
        }
    };

    const handlePersonalInfoChange = (field: string, value: string) => {
        setPersonalInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            const profileData = {
                allergies,
                dislikes,
                personalInfo,
                nutritionalPreferences,
                recommendedCalories: calculateRecommendedCalories(),
                lastSaved: new Date().toISOString()
            };

            // Guardar en localStorage (fallback)
            localStorage.setItem('userProfile', JSON.stringify(profileData));

            // Guardar en backend si está loggeado
            if (isLoggedIn && token) {
                try {
                    const preferencesPayload = {
                        allergies: allergies,
                        dislikedIngredients: dislikes,
                        weightGoal: personalInfo.weightGoal.toUpperCase() as 'LOSE' | 'MAINTAIN' | 'GAIN',
                        nutritionalPreferences: nutritionalPreferences,
                        age: personalInfo.age ? parseInt(personalInfo.age) : undefined,
                        height: personalInfo.height ? parseInt(personalInfo.height) : undefined,
                        weight: personalInfo.weight ? parseInt(personalInfo.weight) : undefined,
                        gender: personalInfo.gender || undefined,
                        activityLevel: personalInfo.activityLevel || undefined,
                        recommendedCalories: calculateRecommendedCalories()
                    };

                    // ✅ CORREGIDO: Pasar el payload como segundo parámetro
                    await userService.updatePreferences(token, preferencesPayload); // ← Cambio aquí

                    // ✅ NUEVO: Forzar recarga de recomendaciones
                    window.dispatchEvent(new CustomEvent('preferencesUpdated', {
                        detail: { timestamp: Date.now() }
                    }));

                    toast({
                        title: "¡Perfil guardado en la nube!",
                        description: "Tus recomendaciones se han actualizado.",
                    });
                } catch (error) {
                    console.error('Error saving to backend:', error);
                    toast({
                        title: "Guardado local",
                        description: "Preferencias guardadas localmente. Error al sincronizar con la nube.",
                        variant: "destructive",
                    });
                }
            } else {
                toast({
                    title: "¡Perfil guardado localmente!",
                    description: "Inicia sesión para sincronizar tus preferencias en la nube.",
                });
            }

            window.dispatchEvent(new CustomEvent('profileUpdated'));

        } catch (error) {
            console.error('Error saving profile:', error);
            toast({
                title: "Error",
                description: "No se pudo guardar el perfil",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }

    };

    const handleGetRecommendations = async () => {
        if (!token) {
            toast({
                title: "Inicia sesión",
                description: "Debes iniciar sesión para obtener recomendaciones personalizadas",
                variant: "destructive",
            });
            return;
        }

        setLoadingRecommendations(true);
        try {
            const recs = await recommendationService.getPersonalizedRecommendations(token, 6);
            setRecommendations(recs);

            if (recs.length === 0) {
                toast({
                    title: "Sin recomendaciones",
                    description: "No encontramos recetas que coincidan con tus preferencias actuales.",
                    variant: "default",
                });
            } else {
                toast({
                    title: "¡Recomendaciones listas!",
                    description: `Encontramos ${recs.length} recetas perfectas para ti.`,
                });
            }
        } catch (error) {
            console.error('Error getting recommendations:', error);
            toast({
                title: "Error",
                description: "No se pudieron cargar las recomendaciones",
                variant: "destructive",
            });
        } finally {
            setLoadingRecommendations(false);
        }
    };

    const hasUnsavedChanges = () => {
        const savedProfile = localStorage.getItem('userProfile');
        if (!savedProfile) return true;

        try {
            const saved = JSON.parse(savedProfile);
            return JSON.stringify({ allergies, dislikes, personalInfo, nutritionalPreferences }) !==
                JSON.stringify({
                    allergies: saved.allergies || [],
                    dislikes: saved.dislikes || [],
                    personalInfo: saved.personalInfo || {},
                    nutritionalPreferences: saved.nutritionalPreferences || []
                });
        } catch {
            return true;
        }
    };

    const recommendedCalories = calculateRecommendedCalories();

    // Mapeo de tags a nombres legibles
    const tagDisplayNames: { [key in Tag]: string } = {
        [Tag.VEGETARIANA]: 'Vegetariana',
        [Tag.VEGANA]: 'Vegana',
        [Tag.ALTA_PROTEINA]: 'Alta en Proteína',
        [Tag.BAJO_EN_CARBOS]: 'Baja en Carbohidratos',
        [Tag.GLUTEN_FREE]: 'Sin Gluten',
        [Tag.SIN_LACTOSA]: 'Sin Lactosa',
        [Tag.RÁPIDA]: 'Rápida de Preparar',
        [Tag.KETO]: 'Keto',
        [Tag.ALTA_FIBRA]: 'Alta en Fibra',
        [Tag.BAJO_EN_CALORIAS]: 'Baja en Calorías',
        [Tag.BAJO_EN_GRASAS]: 'Baja en Grasas'
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/')}
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-xl font-semibold text-gray-900">Configuración de Perfil</h1>
                        </div>
                        <div className="flex gap-2">
                            {/* <Button
                                onClick={handleGetRecommendations}
                                disabled={loadingRecommendations || !token || !isLoggedIn}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <Sparkles className="h-4 w-4" />
                                {loadingRecommendations ? 'Buscando...' : 'Ver Recomendaciones'}
                            </Button> */}
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className={`flex items-center gap-2 ${hasUnsavedChanges()
                                    ? 'bg-primary hover:bg-primary/90'
                                    : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                {saving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        {isLoggedIn ? <Cloud className="h-4 w-4" /> : <CloudOff className="h-4 w-4" />}
                                        <Save className="h-4 w-4" />
                                        {hasUnsavedChanges() ? 'Guardar Cambios' : 'Guardado ✓'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Sección de Recomendaciones */}
                {recommendations.length > 0 && (
                    <Card className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-purple-700">
                                <Sparkles className="h-5 w-5" />
                                Recetas Recomendadas para Ti
                            </CardTitle>
                            <p className="text-sm text-purple-600">
                                Basado en tus preferencias y metas actuales
                            </p>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {recommendations.map(recipe => (
                                    <RecipeCard
                                        key={recipe.id}
                                        {...recipe}
                                        showRecommendationBadge={true}
                                    />
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Información Personal */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-blue-600 flex items-center gap-2">
                                📊 Información Personal
                            </CardTitle>
                            <p className="text-sm text-gray-600">
                                Ingresa tus datos personales para calcular tu ingesta diaria recomendada de calorías.
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="age">Edad</Label>
                                    <Input
                                        id="age"
                                        type="number"
                                        placeholder="25"
                                        value={personalInfo.age}
                                        onChange={(e) => handlePersonalInfoChange('age', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="weight">Peso (kg)</Label>
                                    <Input
                                        id="weight"
                                        type="number"
                                        placeholder="70"
                                        value={personalInfo.weight}
                                        onChange={(e) => handlePersonalInfoChange('weight', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="height">Estatura (cm)</Label>
                                <Input
                                    id="height"
                                    type="number"
                                    placeholder="175"
                                    value={personalInfo.height}
                                    onChange={(e) => handlePersonalInfoChange('height', e.target.value)}
                                />
                            </div>

                            <div>
                                <Label htmlFor="gender">Género</Label>
                                <Select value={personalInfo.gender} onValueChange={(value) => handlePersonalInfoChange('gender', value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecciona género" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">Masculino</SelectItem>
                                        <SelectItem value="female">Femenino</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="activity">Nivel de Actividad</Label>
                                <Select value={personalInfo.activityLevel} onValueChange={(value) => handlePersonalInfoChange('activityLevel', value)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecciona nivel de actividad" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="sedentary">Sedentario (poco/nada de ejercicio)</SelectItem>
                                        <SelectItem value="light">Ligero (1-3 días/semana)</SelectItem>
                                        <SelectItem value="moderate">Moderado (3-5 días/semana)</SelectItem>
                                        <SelectItem value="very">Muy activo (6-7 días/semana)</SelectItem>
                                        <SelectItem value="extreme">Extremadamente activo (2x/día)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Objetivo de Peso</Label>
                                <RadioGroup
                                    value={personalInfo.weightGoal}
                                    onValueChange={(value) => handlePersonalInfoChange('weightGoal', value)}
                                    className="mt-2"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="lose" id="lose" />
                                        <label htmlFor="lose" className="text-sm font-medium">Perder Peso (-500 cal/día)</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="maintain" id="maintain" />
                                        <label htmlFor="maintain" className="text-sm font-medium">Mantener Peso</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="gain" id="gain" />
                                        <label htmlFor="gain" className="text-sm font-medium">Subir Peso (+500 cal/día)</label>
                                    </div>
                                </RadioGroup>
                            </div>

                            {recommendedCalories > 0 && (
                                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border border-blue-200">
                                    <h4 className="font-medium text-blue-800 mb-1">
                                        {personalInfo.weightGoal === 'lose' && 'Objetivo de Pérdida de Peso'}
                                        {personalInfo.weightGoal === 'gain' && 'Objetivo de Ganancia de Peso'}
                                        {personalInfo.weightGoal === 'maintain' && 'Objetivo de Mantenimiento'}
                                    </h4>
                                    <p className="text-2xl font-bold text-blue-600">{recommendedCalories} calorías/día</p>
                                    <p className="text-sm text-blue-700 mt-1">
                                        {personalInfo.weightGoal === 'lose' && 'Para una pérdida saludable de 0.5 kg/semana'}
                                        {personalInfo.weightGoal === 'gain' && 'Para una ganancia saludable de 0.5 kg/semana'}
                                        {personalInfo.weightGoal === 'maintain' && 'Para mantener tu peso actual'}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Alergias */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-red-600 flex items-center gap-2">
                                ⚠️ Alergias e Intolerancias
                            </CardTitle>
                            <p className="text-sm text-gray-600">
                                Selecciona cualquier alergia o intolerancia alimentaria que tengas.
                            </p>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {commonAllergens.map((allergen) => (
                                    <div key={allergen} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`allergy-${allergen}`}
                                            checked={allergies.includes(allergen)}
                                            onCheckedChange={(checked) => handleAllergyChange(allergen, checked as boolean)}
                                        />
                                        <label
                                            htmlFor={`allergy-${allergen}`}
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            {allergen}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ingredientes que No Me Gustan */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-orange-600 flex items-center gap-2">
                                👎 Ingredientes que No Me Gustan
                            </CardTitle>
                            <p className="text-sm text-gray-600">
                                Selecciona ingredientes que no te gustan.
                            </p>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {commonDislikes.map((dislike) => (
                                    <div key={dislike} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`dislike-${dislike}`}
                                            checked={dislikes.includes(dislike)}
                                            onCheckedChange={(checked) => handleDislikeChange(dislike, checked as boolean)}
                                        />
                                        <label
                                            htmlFor={`dislike-${dislike}`}
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            {dislike}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Preferencias Nutricionales */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-green-600 flex items-center gap-2">
                                🌱 Preferencias Nutricionales
                            </CardTitle>
                            <p className="text-sm text-gray-600">
                                Selecciona tus preferencias dietéticas para recomendaciones más precisas.
                            </p>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {nutritionalTags.map((tag) => (
                                    <div key={tag} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`pref-${tag}`}
                                            checked={nutritionalPreferences.includes(tag)}
                                            onCheckedChange={(checked) => handleNutritionalPreferenceChange(tag, checked as boolean)}
                                        />
                                        <label
                                            htmlFor={`pref-${tag}`}
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            {tagDisplayNames[tag]}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Resumen */}
                {(allergies.length > 0 || dislikes.length > 0 || nutritionalPreferences.length > 0 || recommendedCalories > 0) && (
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle>Resumen de tus Preferencias</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {recommendedCalories > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-medium text-blue-600 mb-2">Meta Diaria de Calorías:</h4>
                                    <span className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full text-lg font-semibold">
                                        {recommendedCalories} calorías
                                    </span>
                                </div>
                            )}

                            {allergies.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-medium text-red-600 mb-2">Alergias e Intolerancias:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {allergies.map((allergen) => (
                                            <span
                                                key={allergen}
                                                className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs"
                                            >
                                                {allergen}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {dislikes.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-medium text-orange-600 mb-2">Ingredientes que No Me Gustan:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {dislikes.map((dislike) => (
                                            <span
                                                key={dislike}
                                                className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs"
                                            >
                                                {dislike}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {nutritionalPreferences.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-medium text-green-600 mb-2">Preferencias Nutricionales:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {nutritionalPreferences.map((pref) => (
                                            <span
                                                key={pref}
                                                className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs"
                                            >
                                                {tagDisplayNames[pref]}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Footer de Guardado */}
                <Card className="mt-6 bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {isLoggedIn ? 'Sincronizar en la Nube' : 'Guardar Localmente'}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {isLoggedIn
                                        ? "Tus preferencias se guardarán en la nube y estarán disponibles en todos tus dispositivos."
                                        : "Inicia sesión para sincronizar tus preferencias en la nube y acceder a ellas desde cualquier dispositivo."
                                    }
                                </p>
                            </div>
                            <div className="flex gap-3">
                                {hasUnsavedChanges() && (
                                    <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                                        Cambios Sin Guardar
                                    </div>
                                )}
                                <Button
                                    onClick={handleSave}
                                    size="lg"
                                    disabled={saving}
                                    className={`flex items-center gap-2 ${hasUnsavedChanges()
                                        ? 'bg-primary hover:bg-primary/90 shadow-lg'
                                        : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            {isLoggedIn ? <Cloud className="h-5 w-5" /> : <CloudOff className="h-5 w-5" />}
                                            <Save className="h-5 w-5" />
                                            {hasUnsavedChanges() ? 'Guardar Cambios' : 'Guardado ✓'}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default Profile;